import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaArrowRight, 
  FaStar, 
  FaTrophy, 
  FaRocket, 
  FaTimes,
  FaCode,
  FaShieldAlt,
  FaLaptop,
  FaChartLine
} from 'react-icons/fa';

const DegreeProgramsBanner = ({ 
  variant = 'top', 
  autoClose = false, 
  autoCloseDelay = 8000 
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [currentText, setCurrentText] = useState(0);

  const adTexts = [
    "🎓 Discover Your Future with Our Degree Programs",
    "🚀 Launch Your Career with Industry-Ready Skills", 
    "💼 Get Accredited Programs & World-Class Education",
    "🏆 Join 95% Success Rate - Apply Now!",
    "⭐ Explore 8+ Programs - Click to Learn More"
  ];

  const programIcons = [FaCode, FaShieldAlt, FaLaptop, FaChartLine, FaGraduationCap];

  useEffect(() => {
    // Show banner after 2 seconds
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    // Auto-rotate text every 3 seconds
    const textTimer = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % adTexts.length);
    }, 3000);

    // Auto-close if enabled
    let autoCloseTimer;
    if (autoClose) {
      autoCloseTimer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);
    }

    return () => {
      clearTimeout(showTimer);
      clearInterval(textTimer);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [autoClose, autoCloseDelay]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleClick = () => {
    navigate('/programs');
  };

  if (!isVisible) return null;

  const positionClasses = {
    'top': 'top-0 left-0 right-0',
    'bottom': 'bottom-0 left-0 right-0'
  };

  return (
    <div className={`fixed ${positionClasses[variant]} z-50 transition-all duration-300 ${
      isClosing ? 'opacity-0 scale-95 translate-y-full' : 'opacity-100 scale-100 translate-y-0'
    }`}>
      <div 
        className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-500 text-white cursor-pointer overflow-hidden group hover:shadow-2xl transition-all duration-300"
        onClick={handleClick}
        style={{
          animation: 'pulse 2s infinite'
        }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-shimmer"></div>
        
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="absolute top-2 right-2 z-10 bg-black/20 hover:bg-black/40 rounded-full p-1.5 transition-colors"
        >
          <FaTimes className="text-white text-sm" />
        </button>

        {/* Content */}
        <div className="relative px-6 py-4 flex items-center justify-between">
          {/* Left Side - Icon and Text */}
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
              <FaGraduationCap className="text-2xl animate-bounce" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Degree Programs</h3>
              <div 
                key={currentText}
                className="text-sm opacity-90 animate-fadeIn"
                style={{
                  background: 'linear-gradient(45deg, #fff, #ffd700, #fff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {adTexts[currentText]}
              </div>
            </div>
          </div>

          {/* Center - Program Icons */}
          <div className="hidden md:flex items-center gap-3">
            {programIcons.map((Icon, index) => (
              <div 
                key={index}
                className="bg-white/20 p-2 rounded-lg group-hover:scale-110 transition-transform"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <Icon className="text-lg" />
              </div>
            ))}
          </div>

          {/* Right Side - Features and CTA */}
          <div className="flex items-center gap-6">
            {/* Features */}
            <div className="hidden lg:flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <FaStar className="text-yellow-300" />
                <span>8+ Programs</span>
              </div>
              <div className="flex items-center gap-1">
                <FaTrophy className="text-yellow-300" />
                <span>95% Success</span>
              </div>
              <div className="flex items-center gap-1">
                <FaRocket className="text-yellow-300" />
                <span>Career Ready</span>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full transition-colors group-hover:scale-105">
              <span className="text-sm font-semibold">Explore Now</span>
              <FaArrowRight className="text-lg animate-pulse" />
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-2 left-4 w-1 h-1 bg-yellow-300 rounded-full animate-ping"></div>
        <div className="absolute bottom-2 right-8 w-1 h-1 bg-white rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-4 right-16 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(5px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default DegreeProgramsBanner;
